// TradeDetailPage — Single trade detail
// Layout: pair hero, P&L, entry/exit, chart, bot decision log, related trades

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlatformLogo } from '../../components/Brand';
import { TfCard, TfCardList, TfSectionHeaderView, TfSparklineMarker } from '../../components/TfPrimitives';

export function TradeDetailPage() {
  const nav = useNavigate();
  const { id } = useParams();

  // Mock trade (in real app, fetch by id)
  const trade = {
    id: id || 't1',
    pair: 'NQ',
    direction: 'long' as const,
    entry: 18245.50,
    exit: 18290.25,
    size: 2,
    pnl: 895.00,
    pnlPct: 0.245,
    duration: '2h 14m',
    opened: '2026-07-24 09:18:00 ET',
    closed: '2026-07-24 11:32:14 ET',
    strategy: 'Breakout',
    bot: 'AI Bot Trader v3.2',
    fees: 4.50,
    takeProfit: 18300,
    stopLoss: 18220,
    rMultiple: 1.6,
    confidence: 0.78,
    riskReward: 1.9,
  };

  // Generate price points
  const points = (() => {
    const n = 60;
    const out: number[] = [];
    let v = trade.entry - 30;
    for (let i = 0; i < n; i++) {
      v += Math.sin(i * 0.15) * 5 + (i < 30 ? -0.3 : 0.6) * (trade.pnl > 0 ? 1 : -1);
      out.push(v);
    }
    out[Math.floor(n / 3)] = trade.entry;
    out[Math.floor(n * 0.85)] = trade.exit;
    return out;
  })();

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">Trade detail</div>
        <button onClick={() => alert('Trade shared')} className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Hero — pair + P&L */}
      <div className="mx-4 mt-4">
        <div
          className="rounded-[20px] text-center p-5 text-white"
          style={{ background: trade.pnl >= 0 ? 'linear-gradient(135deg, #34C759 0%, #1E7E34 100%)' : 'linear-gradient(135deg, #FF3B30 0%, #B82B22 100%)' }}
        >
          <div className="flex items-center justify-center gap-2">
            <div className="text-[11px] uppercase tracking-wider text-white/80">{trade.pair} · {trade.direction.toUpperCase()}</div>
            <span className="rounded-full bg-white/20 backdrop-blur-md px-1.5 py-0.5 text-[9px] font-bold uppercase">{trade.strategy}</span>
          </div>
          <div className="mt-1 text-[40px] font-bold tabular-nums">{trade.pnl >= 0 ? '+' : ''}${Math.abs(trade.pnl).toFixed(2)}</div>
          <div className="mt-1 text-[13px] text-white/85">{trade.pnl >= 0 ? '+' : ''}{trade.pnlPct.toFixed(2)}% · {trade.duration}</div>
        </div>
      </div>

      {/* Price chart */}
      <TfSectionHeaderView title="Price action" />
      <TfCard className="mx-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-tf-text-3">Entry</div>
            <div className="text-[14px] font-bold tabular-nums text-tf-blue">{trade.entry.toFixed(2)}</div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-tf-text-3">R multiple</div>
            <div className="text-[14px] font-bold tabular-nums text-tf-text-1">{trade.rMultiple.toFixed(1)}R</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-tf-text-3">Exit</div>
            <div className={['text-[14px] font-bold tabular-nums', trade.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>{trade.exit.toFixed(2)}</div>
          </div>
        </div>
        <div className="-mx-1">
          <TfSparklineMarker
            points={points}
            color={trade.pnl >= 0 ? '#34C759' : '#FF3B30'}
            height={140}
            markerIndex={Math.floor(points.length * 0.85)}
            markerLabel={trade.exit.toFixed(2)}
            markerPct={trade.pnlPct}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-[9px] text-tf-text-3">
          <span>09:18</span>
          <span>10:00</span>
          <span>11:00</span>
          <span>11:32</span>
        </div>
      </TfCard>

      {/* Stats grid */}
      <TfSectionHeaderView title="Stats" />
      <div className="mx-4 grid grid-cols-2 gap-2">
        <Stat label="Size" value={`${trade.size} contracts`} />
        <Stat label="Fees" value={`$${trade.fees.toFixed(2)}`} />
        <Stat label="Take profit" value={trade.takeProfit.toFixed(2)} color="text-tf-profit" />
        <Stat label="Stop loss" value={trade.stopLoss.toFixed(2)} color="text-tf-loss" />
        <Stat label="Risk:Reward" value={`1 : ${trade.riskReward.toFixed(1)}`} />
        <Stat label="Bot confidence" value={`${(trade.confidence * 100).toFixed(0)}%`} color="text-tf-blue" />
      </div>

      {/* Timing */}
      <TfSectionHeaderView title="Timing" />
      <div className="mx-4">
        <TfCardList>
          <Row label="Opened"  value={trade.opened} />
          <Row label="Closed"  value={trade.closed} />
          <Row label="Duration" value={trade.duration} last />
        </TfCardList>
      </div>

      {/* Bot decision log */}
      <TfSectionHeaderView title="Bot decision" viewAllLabel="Replay" onViewAll={() => nav('/portal/chart')} />
      <div className="mx-4">
        <TfCardList>
          <Decision time="09:18:00" icon="📊" text="Detected breakout above 18,240" />
          <Decision time="09:18:02" icon="🎯" text="Long entry signal · confidence 78%" />
          <Decision time="09:18:03" icon="✅" text="Filled at 18,245.50 (2 contracts)" />
          <Decision time="09:30:00" icon="🛡️" text="Stop loss set to 18,220 (-25 pts)" />
          <Decision time="09:30:00" icon="🎯" text="Take profit set to 18,300 (+55 pts)" />
          <Decision time="11:32:14" icon="💰" text="Take profit hit · closed at 18,290.25" last />
        </TfCardList>
      </div>

      {/* Platform */}
      <TfSectionHeaderView title="Platform" />
      <div className="mx-4">
        <TfCardList>
          <div className="flex items-center gap-3 px-4 py-3">
            <PlatformLogo name="deepchart" size={28} />
            <div className="flex-1">
              <div className="text-[14px] font-semibold text-tf-text-1">Deepchart®</div>
              <div className="text-[11px] text-tf-text-3">{trade.bot}</div>
            </div>
          </div>
        </TfCardList>
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="rounded-[12px] border border-tf-border bg-tf-bg p-3">
      <div className="text-[11px] uppercase tracking-wide text-tf-text-3">{label}</div>
      <div className={['mt-1 text-[16px] font-bold tabular-nums', color || 'text-tf-text-1'].join(' ')}>{value}</div>
    </div>
  );
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-2.5', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[12px] text-tf-text-3">{label}</span>
      <span className="text-[13px] font-medium text-tf-text-1">{value}</span>
    </div>
  );
}

function Decision({ time, icon, text, last }: { time: string; icon: string; text: string; last?: boolean }) {
  return (
    <div className={['flex items-start gap-3 px-4 py-2.5', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[16px] mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-tf-text-1">{text}</div>
        <div className="text-[10px] text-tf-text-3 font-mono">{time}</div>
      </div>
    </div>
  );
}
